import React from 'react';
import {
  Card,
  makeStyles,
  CardContent,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { verySmall, small, medium, large, veryLarge } from '../constants';
import OwnershipIcon from '@material-ui/icons/AccountBalance';
import RuralIcon from '@material-ui/icons/House';
import SuburbanIcon from '@material-ui/icons/HomeWork';
import CityIcon from '@material-ui/icons/LocationCity';
import TownIcon from '@material-ui/icons/EmojiTransportation';
import PeopleIcon from '@material-ui/icons/People';
import Years1Icon from '@material-ui/icons/Filter1';
import Years2Icon from '@material-ui/icons/Filter2';
import Years3Icon from '@material-ui/icons/Filter3';
import Years4Icon from '@material-ui/icons/Filter4';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { minBy, maxBy, upperCase } from 'lodash';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  card: {
    // marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
  label: {
    paddingBottom: theme.spacing(0.5),
    textTransform: 'upperCase',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    fontSize: 10,
  },
  graduationGT90: {
    color: theme.palette.success.main,
  },
  graduationGT70: {
    color: theme.palette.info.main,
  },
}));

const ResearchCard = ({ data, user, addSchool, student, history }) => {
  const classes = useStyles();
  const canEdit = user.type !== 'parent';
  const isStudent = user.type === 'student';
  const routePrefix = isStudent ? '' : `/students/${student.id}`;
  const [gradColor, setGradColor] = React.useState('inherit');

  const getGradColor = () => {
    const val = data['latest.completion.consumer_rate']
      ? (Math.abs(data['latest.completion.consumer_rate']) * 100).toFixed(2)
      : null;
    if (val) {
      if (val >= 90) return 'textPrimary';
      if (val >= 70) return 'textSecondary';
      if (val < 40) return 'secondary';
      return 'primary';
    }

    return null;
  };

  React.useEffect(() => {
    const color = getGradColor();
    setGradColor(color);
  }, []);

  const onAdd = () => {
    let college = {
      label: data['school.name'],
      value: String(data?.id),
    };
    addSchool('student', college).then(() =>
      history.push(`${routePrefix}/schools`),
    );
  };

  const earningsData =
    data['latest.programs.cip_4_digit']?.length > 1
      ? [
          minBy(data['latest.programs.cip_4_digit'], 'earnings.median_earnings')
            ?.earnings?.median_earnings,
          maxBy(data['latest.programs.cip_4_digit'], 'earnings.median_earnings')
            ?.earnings?.median_earnings,
        ]
      : null;

  const getYearIcon = (year) => (
    <Box display="flex" alignItems="center" flexBasis="50%">
      {(year === 4 && (
        <Years4Icon fontSize="small" className={classes.icon} />
      )) ||
        (year === 1 && (
          <Years1Icon fontSize="small" className={classes.icon} />
        )) ||
        (year === 3 && (
          <Years3Icon fontSize="small" className={classes.icon} />
        )) ||
        (year === 2 && (
          <Years2Icon fontSize="small" className={classes.icon} />
        )) ||
        (!year && <Typography variant="subtitle1">N/A</Typography>)}
      <Typography variant="caption">{year > 1 ? 'Years' : 'Year'}</Typography>
    </Box>
  );

  const getLocaleIcon = (locale) => (
    <Box display="flex" alignItems="center" flexBasis="50%">
      {(locale > 40 && (
        <RuralIcon fontSize="small" className={classes.icon} />
      )) ||
        (locale > 30 && (
          <TownIcon fontSize="small" className={classes.icon} />
        )) ||
        (locale > 20 && (
          <SuburbanIcon fontSize="small" className={classes.icon} />
        )) ||
        (locale > 10 && (
          <CityIcon fontSize="small" className={classes.icon} />
        )) ||
        (!locale && <Typography variant="subtitle1">N/A</Typography>)}
      <Typography variant="caption">
        {(locale > 40 && 'Rural') ||
          (locale > 30 && 'Town') ||
          (locale > 20 && 'Suburban') ||
          (locale > 10 && 'City')}
      </Typography>
    </Box>
  );

  const getSize = (value) => (
    <Box display="flex" alignItems="center" flexBasis="50%">
      <PeopleIcon fontSize="small" className={classes.icon} />
      <Typography variant="caption">
        {(verySmall.includes(value) && 'Very Small') ||
          (small.includes(value) && 'Small') ||
          (medium.includes(value) && 'Medium') ||
          (large.includes(value) && 'Large') ||
          (veryLarge.includes(value) && 'Very Large') ||
          'NA'}
      </Typography>
    </Box>
  );

  return (
    <Box className={classes.card}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" marginBottom={2}>
          <Typography variant="h3">{data['school.name']}</Typography>
          {canEdit && (
            <Box marginLeft={2}>
              <IconButton size="small" onClick={onAdd}>
                <AddCircleIcon color="primary" />
              </IconButton>
            </Box>
          )}
        </Box>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <LocationOnIcon fontSize="small" className={classes.icon} />
          <Typography variant="caption">
            {data['school.city'] || `N/A`},{data['school.state'] || `N/A`}
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
          marginBottom={{ xs: 2, sm: 4 }}
        >
          <Box display="flex" alignItems="center" flexBasis="50%">
            <OwnershipIcon className={classes.icon} fontSize="small" />
            <Typography variant="caption">
              {data['school.ownership'] === 1 ? 'Public' : 'Private'}
            </Typography>
          </Box>
          {getYearIcon(data['school.degrees_awarded.highest'])}
          {getLocaleIcon(data['school.locale'])}
          {getSize(data['school.carnegie_size_setting'])}
        </Box>
        <Divider />
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          marginTop={{ xs: 2, sm: 4 }}
        >
          <Box>
            <Typography className={classes.label}>Graduation rate</Typography>
            <Typography
              variant="subtitle1"
              color={gradColor || 'inherit'}
              classes={{
                colorTextPrimary: classes.graduationGT90,
                colorTextSecondary: classes.graduationGT70,
              }}
            >
              {data['latest.completion.consumer_rate']
                ? `${(
                    Math.abs(data['latest.completion.consumer_rate']) * 100
                  ).toFixed(2)}%`
                : `N/A`}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.label}>
              Salary After Completing
            </Typography>
            <Typography variant="subtitle1">
              {earningsData && earningsData[0] !== undefined
                ? `$${earningsData[0]?.toLocaleString()}-$${earningsData[1]?.toLocaleString()}`
                : `N/A`}
            </Typography>
          </Box>
          <Box flexGrow={{ xs: 2, sm: 0 }} marginTop={{ xs: 2, sm: 0 }}>
            <Typography className={classes.label}>
              Average Annual Cost
            </Typography>
            <Typography variant="subtitle1">
              {data['latest.cost.avg_net_price.overall'] ||
              data['latest.cost.tuition.program_year'] ? (
                <b>
                  $
                  {data[
                    'latest.cost.avg_net_price.overall'
                  ]?.toLocaleString() ||
                    data['latest.cost.tuition.program_year']?.toLocaleString()}
                </b>
              ) : (
                'N/A'
              )}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Box>
  );
};

export default ResearchCard;
